import { AnyAction } from 'redux';
import { FormikErrors } from 'formik';
import { HandledError, PaginatedResponse } from '@synergy/api';
import { Media } from '@synergy/models';
import lodashSet from 'lodash/set';

import { BankAccountErrorCode, BankAccountErrorCodeMessageId } from '../../../../models/bank-account-error-code';
import { BankAccountFormValues } from '../../../../models/bank-account-form-values';
import { BlacklistedSupplier } from '../models/blacklisted-supplier';
import {
    CUSTOMER_DETAIL_BANK_ACCOUNT_CREATED,
    CUSTOMER_DETAIL_BANK_ACCOUNT_DELETE_FINISHED,
    CUSTOMER_DETAIL_BANK_ACCOUNT_ERROR,
    CUSTOMER_DETAIL_BANK_ACCOUNT_UPDATED,
    CUSTOMER_DETAIL_BANK_ACCOUNT_UPLOAD_FILE_FINISHED,
    CUSTOMER_DETAIL_BLACKLISTED_SUPPLIERS_RECEIVE,
    CUSTOMER_DETAIL_OVERVIEW_ERROR,
    CUSTOMER_DETAIL_OVERVIEW_RECEIVE_DETAILS,
    CUSTOMER_DETAIL_OVERVIEW_UPDATE,
    CUSTOMER_DETAIL_REMOVED_PROFILE_IMAGE,
    CUSTOMER_DETAIL_UPLOAD_PROFILE_FINISHED,
} from './customer-detail-overview-actions';
import { CUSTOMER_DETAIL_TOGGLE_LOCK_CUSTOMER } from '../../store/customer-detail-actions';
import { CustomerDetailOverviewFormValues } from './../models/customer-detail-overview-form-values';
import { EmailErrorCode, EmailErrorCodeMessageId } from '../../../../models/email-error-code';

export interface CustomerDetailOverviewReducerState {
    customer?: CustomerDetailOverviewFormValues;
    blacklistedSuppliers: PaginatedResponse<BlacklistedSupplier>;
    bankAccounts: BankAccountFormValues[];
    isValid: boolean;
    isDirty: boolean;
    overviewErrors: FormikErrors<CustomerDetailOverviewFormValues>;
    bankAccountErrors: FormikErrors<BankAccountFormValues>;
    /**
     * The customer as the server last handed it over (#2159).
     *
     * `customer` cannot serve as the unsaved-changes baseline: leaving a tab
     * parks its input right there, so the two become the same thing and nothing
     * reads as unsaved any more. This is only written when the server answers.
     */
    baseline?: CustomerDetailOverviewFormValues;
}

const initialCustomerReducerState: CustomerDetailOverviewReducerState = {
    blacklistedSuppliers: { limit: 10, page: 0, total: 0, items: [] },
    bankAccounts: [],
    isValid: true,
    isDirty: false,
    overviewErrors: {},
    bankAccountErrors: {},
};

export const customerDetailOverviewReducer = (
    state: CustomerDetailOverviewReducerState = initialCustomerReducerState,
    action: AnyAction
): CustomerDetailOverviewReducerState => {
    switch (action.type) {
        case CUSTOMER_DETAIL_OVERVIEW_RECEIVE_DETAILS: {
            return {
                ...state,
                customer: action['customer'],
                // The same values, kept apart from the ones the tabs edit.
                baseline: action['customer'],
                bankAccounts: action['bankAccounts'],
                overviewErrors: {},
            };
        }

        case CUSTOMER_DETAIL_OVERVIEW_ERROR: {
            const formErrors: FormikErrors<CustomerDetailOverviewFormValues> = { ...state.overviewErrors };
            (action['errorData'] as HandledError<EmailErrorCode>[])?.forEach(error => {
                lodashSet(formErrors, error.property, EmailErrorCodeMessageId[error.error]);
            });

            return {
                ...state,
                isValid: false,
                overviewErrors: formErrors,
            };
        }

        case CUSTOMER_DETAIL_BANK_ACCOUNT_ERROR: {
            const formErrors: FormikErrors<BankAccountFormValues> = { ...state.bankAccountErrors };
            (action['errorData'] as HandledError<BankAccountErrorCode>[])?.forEach(error => {
                lodashSet(formErrors, error.property, BankAccountErrorCodeMessageId[error.error]);
            });

            return {
                ...state,
                bankAccountErrors: formErrors,
            };
        }

        case CUSTOMER_DETAIL_BANK_ACCOUNT_UPLOAD_FILE_FINISHED: {
            const media: Media = action['media'];
            const duplicatedBankAccounts = [...state.bankAccounts];
            const index = duplicatedBankAccounts.findIndex(account => account.id === action['bankAccountId']);
            if (index >= 0) {
                duplicatedBankAccounts[index].media = [...duplicatedBankAccounts[index].media, media];
            }

            return {
                ...state,
                bankAccounts: duplicatedBankAccounts,
            };
        }

        case CUSTOMER_DETAIL_BANK_ACCOUNT_DELETE_FINISHED: {
            return {
                ...state,
                bankAccounts: state.bankAccounts.filter(bankAccount => bankAccount.id !== action['id']),
                bankAccountErrors: {},
            };
        }

        case CUSTOMER_DETAIL_BANK_ACCOUNT_UPDATED: {
            const duplicatedBankAccounts = [...state.bankAccounts];
            const index = duplicatedBankAccounts.findIndex(account => account.id === action['bankAccount'].id);

            if (index >= 0) {
                duplicatedBankAccounts[index] = action['bankAccount'];
            }

            return {
                ...state,
                bankAccounts: duplicatedBankAccounts,
                bankAccountErrors: {},
            };
        }

        case CUSTOMER_DETAIL_BANK_ACCOUNT_CREATED: {
            return {
                ...state,
                bankAccounts: [...state.bankAccounts, action['newBankAccount']],
                bankAccountErrors: {},
            };
        }

        case CUSTOMER_DETAIL_OVERVIEW_UPDATE: {
            const { formData, isDirty, isValid, errors } = action;

            const nextCustomer = {
                ...state.customer,
                ...formData,
            };

            return {
                ...state,
                customer: nextCustomer,
                isDirty: state.isDirty || isDirty,
                isValid,
                overviewErrors: errors,
            };
        }

        case CUSTOMER_DETAIL_BLACKLISTED_SUPPLIERS_RECEIVE: {
            return {
                ...state,
                blacklistedSuppliers: action['blacklistedSuppliers'],
            };
        }

        case CUSTOMER_DETAIL_UPLOAD_PROFILE_FINISHED: {
            if (!state.customer) {
                return state;
            }

            const duplicatedCustomer = { ...state.customer };
            duplicatedCustomer.image = (action['media'] as Media).pathname;

            return {
                ...state,
                customer: duplicatedCustomer,
                // The picture is already on the server; without this the mask
                // would report itself unsaved for the rest of the session.
                baseline: state.baseline ? { ...state.baseline, image: duplicatedCustomer.image } : state.baseline,
            };
        }

        case CUSTOMER_DETAIL_REMOVED_PROFILE_IMAGE: {
            if (!state.customer) {
                return state;
            }

            return {
                ...state,
                customer: {
                    ...state.customer,
                    image: undefined,
                },
                baseline: state.baseline ? { ...state.baseline, image: undefined } : state.baseline,
            };
        }

        case CUSTOMER_DETAIL_TOGGLE_LOCK_CUSTOMER: {
            if (!state.customer) {
                return state;
            }

            return {
                ...state,
                customer: {
                    ...state.customer,
                    isLocked: !state.customer?.isLocked,
                },
                baseline: state.baseline ? { ...state.baseline, isLocked: !state.customer.isLocked } : state.baseline,
            };
        }

        default: {
            return state;
        }
    }
};
